import { useMemo } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IAiAssistantStatus } from "../models/aiAssistantModels";
import { normalizeStatus } from "../utilities/aiAssistantDataUtility";

/**
 * GET AiAssistant/GetStatus for the selected studio: whether the assistant is on, how much of
 * the month's budget is used, and the composer's limits. Shared by SupportHome (which page to
 * show) and the assistant page, through the same query key.
 */
export const useAiAssistantStatus = (enabled = true) => {
  const { selectedStudio } = useAppContext();

  const { data, isLoading, isError } = useApiQuery<unknown>(
    {
      queryKey: [endpointsMetadata.aiAssistant.getStatus, selectedStudio?.id],
      endpoint: endpointsMetadata.aiAssistant.getStatus,
      globalIsLoading: false,
      enabled,
    },
    // One quick retry: on an error the support page falls back to the tickets list, and the
    // default three retries would keep the skeleton up for several seconds first.
    { retry: 1, retryDelay: 500, staleTime: 60_000 },
  );

  const status = useMemo<IAiAssistantStatus | null>(
    () => (data !== undefined && data !== null ? normalizeStatus(data) : null),
    [data],
  );

  return {
    status,
    isLoading: enabled && isLoading,
    isError,
  };
};
